/**
 * Strategem projectile / overlay FX: keep battlefield HP at pre-hit values until damage pop + countdown.
 */

import { getBattlefieldUnitAtZoneIndex } from '../utils/battleLethalDeathFx.js';
import {
  doesStrategemDealDamage,
  doesStrategemDestroyUnits,
  doesStrategemDestroyUnitsById,
  getStrategemDamageAmountById,
} from '../utils/strategemUtils.js';
import { enrichDeploySlotDamageMeta, collectBattlefieldHealthHoldTargetsFromStateTransition } from './deployHealthDisplayHold.js';

export function shouldHoldHealthForStrategemAnimation(anim) {
  if (!anim || anim.targetType !== 'unit' || anim.effectType === 'heal') {
    return false;
  }
  if (
    anim.targetInstanceId
    && typeof anim.healthBefore === 'number'
    && typeof anim.healthAfter === 'number'
    && anim.healthBefore > anim.healthAfter
  ) {
    return true;
  }
  const ghost = anim.ghostUnit;
  if (!ghost?.instanceId || typeof ghost.health !== 'number' || ghost.health <= 0) {
    return false;
  }
  return true;
}

/** True when unit has a shield that will block incoming strategem damage (matches Shield.hasBlockingShield). */
export function unitHasBlockingShieldForStrategemFx(unit) {
  if (!unit || unit.hasShield === false) {
    return false;
  }
  if (unit.hasShield === true) {
    return true;
  }
  return Array.isArray(unit.abilities) && unit.abilities.includes('Shield');
}

/**
 * Whether a unit-targeting strategem hit is fully blocked by an active shield.
 * Uses pre-hit ghost snapshot first, then live unit at projectile impact.
 */
export function isStrategemShieldDeflectedHit({
  unitSnapshot = null,
  liveUnit = null,
  damageAmount = null,
  healthBefore = null,
}) {
  if (typeof damageAmount !== 'number' || damageAmount <= 0) {
    return false;
  }
  if (
    unitHasBlockingShieldForStrategemFx(unitSnapshot)
    || unitHasBlockingShieldForStrategemFx(liveUnit)
  ) {
    return true;
  }
  if (
    unitSnapshot?.hasShield === true
    && liveUnit?.hasShield === false
    && typeof healthBefore === 'number'
    && typeof liveUnit.health === 'number'
    && liveUnit.health === healthBefore
  ) {
    return true;
  }
  return false;
}

/**
 * Attach pre/post HP metadata at strategem registration (before two-phase effect applies).
 */
export function enrichStrategemUnitDamageMeta(anim, damageAmount) {
  if (!shouldHoldHealthForStrategemAnimation(anim)) {
    return anim;
  }
  const healthBefore = anim.healthBefore ?? anim.ghostUnit.health;
  const targetInstanceId = anim.targetInstanceId ?? anim.ghostUnit.instanceId;
  if (typeof damageAmount !== 'number' || damageAmount <= 0) {
    return { ...anim, healthBefore, targetInstanceId };
  }
  if (
    isStrategemShieldDeflectedHit({
      unitSnapshot: anim.ghostUnit,
      damageAmount,
      healthBefore,
    })
  ) {
    return {
      ...anim,
      targetInstanceId,
      healthBefore,
      healthAfter: healthBefore,
      damageAmount: 0,
      shieldDeflected: true,
    };
  }
  return {
    ...anim,
    targetInstanceId,
    healthBefore,
    healthAfter: Math.max(0, healthBefore - damageAmount),
    damageAmount,
  };
}

function resolveOpponentIdFromState(gameState, viewerUserId) {
  const players = gameState?.players || Object.keys(gameState?.state || {});
  return players.find((id) => String(id) !== String(viewerUserId)) ?? null;
}

/**
 * Capture pre-hit unit snapshot from pre-effect game state (opponent-initiated strategem mirror).
 */
export function resolveStrategemGhostUnitFromPreEffectState(
  preEffectState,
  viewerUserId,
  toSlot,
  targetInstanceId = null
) {
  if (!preEffectState?.state || !toSlot || toSlot.index == null) {
    return null;
  }
  const opponentId = resolveOpponentIdFromState(preEffectState, viewerUserId);
  const unit = getBattlefieldUnitAtZoneIndex(
    preEffectState,
    viewerUserId,
    opponentId,
    toSlot.zone,
    toSlot.index
  );
  if (!unit) {
    return null;
  }
  if (targetInstanceId != null && unit.instanceId !== targetInstanceId) {
    return null;
  }
  return { ...unit };
}

/**
 * Normalize enriched strategem animation into hold/countdown slot metadata (Guided Missile 317, etc.).
 */
export function resolveStrategemHealthHoldTargets(anim) {
  if (!shouldHoldHealthForStrategemAnimation(anim)) {
    return [];
  }
  if (anim.shieldDeflected === true) {
    return [];
  }

  const targetInstanceId = anim.targetInstanceId ?? anim.ghostUnit?.instanceId;
  const healthBefore = typeof anim.healthBefore === 'number'
    ? anim.healthBefore
    : anim.ghostUnit?.health;
  let healthAfter = anim.healthAfter;
  const damageAmount = anim.damageAmount;

  if (
    typeof healthAfter !== 'number'
    && typeof healthBefore === 'number'
    && typeof damageAmount === 'number'
    && damageAmount > 0
  ) {
    healthAfter = Math.max(0, healthBefore - damageAmount);
  }

  if (
    !targetInstanceId
    || typeof healthBefore !== 'number'
    || typeof healthAfter !== 'number'
    || healthBefore <= healthAfter
  ) {
    return [];
  }

  const targetZone = anim.toSlot?.zone;
  const targetIndex = anim.toSlot?.index;
  if (targetZone == null || targetIndex == null) {
    throw new Error(
      `resolveStrategemHealthHoldTargets: missing target slot for ${targetInstanceId}`
    );
  }

  return [{
    targetInstanceId,
    healthBefore,
    healthAfter,
    damageAmount,
    ghostUnit: anim.ghostUnit,
    targetZone,
    targetIndex,
  }];
}

/**
 * Compare pre/post strategem game state and attach HP metadata (same pattern as deploy VFX).
 */
export function enrichStrategemAnimationDamageMeta(anim, prevState, nextState, viewerUserId) {
  if (!anim?.toSlot || anim.targetType !== 'unit' || anim.effectType === 'heal') {
    return anim;
  }
  if (anim.toSlot.zone === 'general' || anim.toSlot.index == null) {
    return anim;
  }
  if (!nextState?.state) {
    return anim;
  }

  const slotMeta = enrichDeploySlotDamageMeta(
    anim.strategemId ?? null,
    anim.toSlot,
    prevState,
    nextState,
    viewerUserId
  );
  if (!slotMeta) {
    return anim;
  }

  return {
    ...anim,
    ...slotMeta,
    targetInstanceId: anim.targetInstanceId ?? slotMeta.targetInstanceId,
    ghostUnit: anim.ghostUnit ?? slotMeta.ghostUnit,
  };
}

export function resolveStrategemHealthHoldsForComplete(
  anim,
  prevState,
  nextState,
  viewerUserId,
  damageAmount = null
) {
  let enriched = enrichStrategemAnimationDamageMeta(anim, prevState, nextState, viewerUserId);
  if (typeof damageAmount === 'number') {
    enriched = enrichStrategemUnitDamageMeta(enriched, damageAmount);
  }
  return resolveStrategemHealthHoldTargets(enriched);
}

/**
 * Apply pre-hit holds when strategem-complete arrives (authoritative HP is about to drop).
 * Mirrors deploy: hold is pinned at state transition, not while HP still matches pre-hit in gameState.
 */
export function applyStrategemCompleteHealthHolds({
  prevState,
  nextState,
  viewerUserId,
  strategemInstanceId = null,
  activeAnimations = [],
  cachedHoldTargets = [],
  resolveDamageAmount = null,
} = {}) {
  if (!prevState || !nextState) {
    return [];
  }

  const holdsByInstance = new Map();
  const mergeTargets = (targets) => {
    if (!Array.isArray(targets)) {
      return;
    }
    targets.forEach((target) => {
      if (target?.targetInstanceId) {
        holdsByInstance.set(target.targetInstanceId, target);
      }
    });
  };

  mergeTargets(cachedHoldTargets);

  const instanceKey = strategemInstanceId != null ? String(strategemInstanceId) : null;
  const matchingAnims = instanceKey
    ? activeAnimations.filter((anim) => String(anim.strategemInstanceId) === instanceKey)
    : activeAnimations;

  matchingAnims.forEach((anim) => {
    const damageAmount = typeof resolveDamageAmount === 'function'
      ? resolveDamageAmount(anim)
      : anim.damageAmount;
    mergeTargets(
      resolveStrategemHealthHoldsForComplete(anim, prevState, nextState, viewerUserId, damageAmount)
    );
  });

  return Array.from(holdsByInstance.values());
}

/** Inferno Drop (319) defers damage FX until napalm overlay completes. */
export const INSTANT_EXECUTE_OVERLAY_DAMAGE_STRATEGEM_ID = 319;

/** How instant-execute strategem HP holds are released once VFX finishes. */
export const INSTANT_EXECUTE_FLUSH_KIND = Object.freeze({
  NAPALM_OVERLAY: 'napalm_overlay',
  STRATEGEM_ANIMATION: 'strategem_animation',
  PULSE_SHOCKWAVE: 'pulse_shockwave',
  PER_UNIT_MISSILE: 'per_unit_missile',
});

const PULSE_SHOCKWAVE_STRATEGEM_IDS = new Set([110, 51, 57]);
const PER_UNIT_MISSILE_STRATEGEM_IDS = new Set([112]);

/**
 * Strategems that land in completedOrders on turn-played/game-update with damage already applied
 * (zone-targeting + no-target plays). Two-phase unit strategems stay in hand until strategem-complete.
 */
export function isInstantExecuteStrategemEntry(strategem, gameState = null) {
  if (!strategem?.id) {
    return false;
  }
  if (strategem.pulseShockwaveFx) {
    return true;
  }
  if (doesStrategemDealDamage(strategem) || doesStrategemDestroyUnits(strategem)) {
    return true;
  }
  if (gameState?.state) {
    const damageAmount = getStrategemDamageAmountById(Number(strategem.id), gameState);
    if (typeof damageAmount === 'number' && damageAmount > 0) {
      return true;
    }
    if (doesStrategemDestroyUnitsById(Number(strategem.id), gameState)) {
      return true;
    }
  }
  return false;
}

/**
 * Only Inferno uses pendingBroadcastStrategemDamageRef (blocks badge until napalm ends).
 * Other instant strategems must not — that ref kept hasActiveProjectileAnimation true without
 * a matching onComplete flush, leaving HP badges pinned at pre-hit values.
 */
export function shouldDeferInstantExecuteDamageToOverlay(strategemId) {
  return Number(strategemId) === INSTANT_EXECUTE_OVERLAY_DAMAGE_STRATEGEM_ID;
}

export function resolveInstantExecuteFlushKind(strategemId, strategem = null) {
  const id = Number(strategemId);
  if (id === INSTANT_EXECUTE_OVERLAY_DAMAGE_STRATEGEM_ID) {
    return INSTANT_EXECUTE_FLUSH_KIND.NAPALM_OVERLAY;
  }
  if (strategem?.pulseShockwaveFx || PULSE_SHOCKWAVE_STRATEGEM_IDS.has(id)) {
    return INSTANT_EXECUTE_FLUSH_KIND.PULSE_SHOCKWAVE;
  }
  if (PER_UNIT_MISSILE_STRATEGEM_IDS.has(id)) {
    return INSTANT_EXECUTE_FLUSH_KIND.PER_UNIT_MISSILE;
  }
  return INSTANT_EXECUTE_FLUSH_KIND.STRATEGEM_ANIMATION;
}

export function detectNewInstantExecuteDamageStrategems(prevState, nextState) {
  if (!nextState?.state) {
    return [];
  }

  const newlyAdded = [];
  for (const playerId of Object.keys(nextState.state)) {
    const prevCompleted = prevState?.state?.[playerId]?.completedOrders ?? [];
    const nextCompleted = nextState.state[playerId]?.completedOrders ?? [];
    const prevKeys = new Set(
      prevCompleted
        .filter(Boolean)
        .map((strategem) => `${strategem.id}-${strategem.instanceId}`)
    );

    nextCompleted.forEach((strategem) => {
      if (!strategem || !isInstantExecuteStrategemEntry(strategem, nextState)) {
        return;
      }
      const key = `${strategem.id}-${strategem.instanceId}`;
      if (!prevKeys.has(key)) {
        newlyAdded.push({
          strategemId: Number(strategem.id),
          strategemInstanceId: strategem.instanceId,
          playerId,
          strategem,
        });
      }
    });
  }

  return newlyAdded;
}

export function findCompletedStrategemEntry(gameState, playerId, strategemInstanceId) {
  if (!gameState?.state || strategemInstanceId == null) {
    return null;
  }
  const completed = gameState.state[playerId]?.completedOrders ?? [];
  return completed.find((entry) => entry && entry.instanceId === strategemInstanceId) ?? null;
}

/**
 * Pin pre-hit HP before instant-execute broadcast strategems apply authoritative damage (Inferno Drop 319, etc.).
 */
/**
 * Build per-slot hold targets for Pulse Shockwave / Culling / Swarm from server `pulseShockwaveFx.hits`.
 */
export function resolvePulseShockwaveHoldTargetsFromFx(
  pulseShockwaveFx,
  prevState,
  nextState,
  viewerUserId
) {
  if (!pulseShockwaveFx || !Array.isArray(pulseShockwaveFx.hits) || !nextState?.state) {
    return [];
  }

  const holdsByInstance = new Map();
  pulseShockwaveFx.hits.forEach((hit) => {
    if (!hit || hit.zone == null || hit.slotIndex == null || hit.targetPlayerId == null) {
      return;
    }
    const isOwnerViewer = String(hit.targetPlayerId) === String(viewerUserId);
    const targetZone = `${isOwnerViewer ? 'your' : 'opponent'}_${hit.zone}`;
    const slotMeta = enrichDeploySlotDamageMeta(
      null,
      {
        zone: targetZone,
        index: hit.slotIndex,
        playerId: isOwnerViewer ? 'your' : 'opponent',
      },
      prevState,
      nextState,
      viewerUserId
    );
    if (!slotMeta?.targetInstanceId) {
      return;
    }
    holdsByInstance.set(slotMeta.targetInstanceId, {
      ...slotMeta,
      targetZone,
      targetIndex: hit.slotIndex,
    });
  });

  return Array.from(holdsByInstance.values());
}

export function resolveInstantExecuteStrategemHealthHolds(prevState, nextState, viewerUserId) {
  const newStrategems = detectNewInstantExecuteDamageStrategems(prevState, nextState);
  if (newStrategems.length === 0) {
    return { holdTargets: [], newStrategems: [] };
  }

  const holdTargets = collectBattlefieldHealthHoldTargetsFromStateTransition(
    prevState,
    nextState,
    viewerUserId
  );

  return { holdTargets, newStrategems };
}

/**
 * Resolve hold targets when Inferno napalm completes (napalm entry first, pending ref fallback).
 */
export function resolveInfernoNapalmHoldTargets(napalm, pendingByInstanceId = null) {
  if (!napalm) {
    return [];
  }
  if (Array.isArray(napalm.holdTargets) && napalm.holdTargets.length > 0) {
    return napalm.holdTargets;
  }
  if (napalm.strategemInstanceId == null || pendingByInstanceId == null) {
    return [];
  }
  const pending = typeof pendingByInstanceId.get === 'function'
    ? pendingByInstanceId.get(String(napalm.strategemInstanceId))
    : pendingByInstanceId[String(napalm.strategemInstanceId)];
  if (Array.isArray(pending?.holdTargets) && pending.holdTargets.length > 0) {
    return pending.holdTargets;
  }
  return [];
}

/**
 * Resolve post-hit HP for survivor countdown when server state may still be pre-hit at projectile impact.
 */
export function resolveSurvivorHealthAfter({
  healthBefore,
  healthAfter,
  damageAmount,
  liveHealth,
}) {
  let resolvedAfter = healthAfter;
  if (
    typeof liveHealth === 'number'
    && typeof healthBefore === 'number'
    && liveHealth < healthBefore
  ) {
    resolvedAfter = liveHealth;
  } else if (resolvedAfter == null && typeof liveHealth === 'number') {
    resolvedAfter = liveHealth;
  }
  if (
    typeof healthBefore === 'number'
    && typeof damageAmount === 'number'
    && damageAmount > 0
    && (resolvedAfter == null || resolvedAfter >= healthBefore)
  ) {
    resolvedAfter = Math.max(0, healthBefore - damageAmount);
  }
  return resolvedAfter;
}

function getLiveUnitHealthAtSlot(gameState, viewerUserId, opponentId, zoneName, index, targetInstanceId) {
  if (!gameState?.state || zoneName == null || index == null || !targetInstanceId) {
    return undefined;
  }
  const zone = zoneName.includes('_front') ? 'front' : zoneName.includes('_flank') ? 'flank' : null;
  if (!zone) {
    return undefined;
  }
  const playerId = zoneName.startsWith('your_') ? viewerUserId : opponentId;
  if (!playerId) {
    return undefined;
  }
  const unit = gameState.state[playerId]?.playedOrders?.[zone]?.[index];
  if (unit?.instanceId === targetInstanceId && typeof unit.health === 'number') {
    return unit.health;
  }
  if (!unit || unit.instanceId !== targetInstanceId) {
    return 0;
  }
  return undefined;
}

/**
 * Authoritative post-hit HP for survivor countdown (uses live slot state when available).
 */
export function resolveCountdownTargetHealth(trigger, gameState, viewerUserId, opponentId) {
  if (!trigger?.targetInstanceId || typeof trigger.healthBefore !== 'number') {
    return trigger?.healthAfter;
  }

  let liveHealth;
  const { targetZone, targetIndex } = trigger;
  if (targetZone != null && targetIndex != null) {
    liveHealth = getLiveUnitHealthAtSlot(
      gameState,
      viewerUserId,
      opponentId,
      targetZone,
      targetIndex,
      trigger.targetInstanceId
    );
  }

  return resolveSurvivorHealthAfter({
    healthBefore: trigger.healthBefore,
    healthAfter: typeof trigger.healthAfter === 'number' ? trigger.healthAfter : undefined,
    damageAmount: typeof trigger.damageAmount === 'number' ? trigger.damageAmount : undefined,
    liveHealth,
  });
}

/**
 * Ghost snapshots are for slot visibility during projectiles — never replace authoritative HP on live units.
 */
export function pickBattlefieldUnitForDisplay(actualOrder, ghostUnit) {
  if (!actualOrder) {
    return ghostUnit ?? null;
  }
  return actualOrder;
}
